// src/components/goal-settings/KpaCascadeModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { fetchManagerKpas } from '../../api/kpas';

const toNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};
const safe = (v) => (v ?? '—');
const safeToFixed = (v, d = 2) => (Number.isFinite(v) ? v.toFixed(d) : '—');

export default function KpaCascadeModal({
                                            show, open, visible,
                                            contractId,
                                            currentTotal = 0,
                                            kpaWeighting = 90,
                                            onClose,
                                            onSubmit, // ({ managerKpaIds })
                                        }) {
    const isOpen = !!(show ?? open ?? visible);

    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [manager, setManager] = useState(null);
    const [managerKpas, setManagerKpas] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        if (!isOpen || !contractId) return;
        (async () => {
            setLoading(true); setErr('');
            try {
                const { manager: mgr, kpas } = await fetchManagerKpas(contractId);
                setManager(mgr || null);
                setManagerKpas(Array.isArray(kpas) ? kpas : []);
            } catch (e) {
                setErr(e?.response?.data?.message || 'Failed to load manager KPAs.');
            } finally {
                setLoading(false);
            }
        })();
    }, [isOpen, contractId]);

    const target = toNum(kpaWeighting, 90);
    const current = toNum(currentTotal, 0);

    const selectedTotal = useMemo(() => {
        return (selectedIds || []).reduce((sum, id) => {
            const row = managerKpas.find(k => String(k.id) === String(id));
            return sum + toNum(row?.weight, 0);
        }, 0);
    }, [selectedIds, managerKpas]);

    const remaining = useMemo(() => {
        return target - (current + selectedTotal);
    }, [target, current, selectedTotal]);

    const toggle = (id) => {
        setSelectedIds(prev => {
            const s = new Set(prev);
            const key = String(id);
            s.has(key) ? s.delete(key) : s.add(key);
            return Array.from(s);
        });
    };
    const selectAll = () => setSelectedIds(managerKpas.map(r => String(r.id)));
    const clearAll = () => setSelectedIds([]);

    const submit = async () => {
        await onSubmit?.({ managerKpaIds: selectedIds.map(id => Number(id)) });
    };

    if (!isOpen) return null;

    return (
        <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,.4)' }} aria-modal="true" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h6 className="modal-title">Cascade KPAs</h6>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {err && <div className="alert alert-danger py-1">{err}</div>}

                        {/* Manager info header */}
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                                <div className="fw-semibold">Manager</div>
                                <div className="text-muted small">
                                    {safe(manager?.name)} &nbsp;·&nbsp; {safe(manager?.email)}
                                </div>
                            </div>
                            <div className="small text-muted">
                                Target: {safeToFixed(target)} &nbsp;•&nbsp; Current: {safeToFixed(current)} &nbsp;•&nbsp;
                                Selected: {safeToFixed(selectedTotal)} &nbsp;•&nbsp; Remaining: {safeToFixed(remaining)}
                            </div>
                        </div>

                        {loading && <div className="text-muted">Loading manager KPAs…</div>}

                        {!loading && managerKpas.length === 0 && (
                            <div className="alert alert-info py-1">
                                No manager KPAs found for the active cycle, or your manager is not set.
                            </div>
                        )}

                        {!loading && managerKpas.length > 0 && (
                            <>
                                <div className="d-flex gap-2 mb-2">
                                    <button className="btn btn-sm btn-outline-secondary" onClick={selectAll}>Select all</button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={clearAll}>Clear</button>
                                </div>

                                <table className="table table-sm align-middle">
                                    <thead>
                                    <tr>
                                        <th style={{ width: 32 }}></th>
                                        <th>Name</th>
                                        <th style={{ width: 110, textAlign: 'right' }}>Weight</th>
                                        <th style={{ width: 140 }}>Timeframe</th>
                                        <th>Alignment</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {managerKpas.map(k => {
                                        const checked = selectedIds.includes(String(k.id));
                                        return (
                                            <tr key={k.id}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={checked}
                                                        onChange={() => toggle(k.id)}
                                                    />
                                                </td>
                                                <td>{k.name}</td>
                                                <td style={{ textAlign: 'right' }}>{toNum(k.weight, 0)}</td>
                                                <td>{k.timeframe?.name ?? '—'}</td>
                                                <td>{k.alignment ?? '—'}</td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>

                    <div className="modal-footer py-2">
                        <button className="btn btn-sm btn-light" onClick={onClose}>Close</button>
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={submit}
                            disabled={!selectedIds.length}
                        >
                            Cascade Selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}